/**
 * Task Kanban Board Component
 *
 * Provides a drag-and-drop Kanban board view for task management.
 * Tasks are organized into columns by status: Not Started, In Progress, Completed, Blocked.
 *
 * @see GitHub Issue #107 - Kanban board view for tasks
 */

import { useState, useMemo } from 'react'
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from '@hello-pangea/dnd'
import {
  Clock,
  PlayCircle,
  CheckCircle,
  XCircle,
  AlertCircle,
  Calendar,
  User,
  Shield,
  Send,
  ThumbsUp,
  ThumbsDown,
  AlertTriangle,
  FileText,
  Building2,
  ClipboardList,
  File,
} from 'lucide-react'
import { Link } from 'react-router-dom'

// ============================================================================
// TYPES
// ============================================================================

interface Task {
  id: string
  title: string
  description?: string
  type: 'evidence-due' | 'document-review' | 'risk-assessment' | 'vendor-assessment' | 'custom'
  assigned_to?: string
  assigned_to_username?: string
  assigned_to_email?: string
  created_by: string
  created_by_username?: string
  priority: 'critical' | 'high' | 'medium' | 'low'
  status: 'open' | 'in-progress' | 'pending-approval' | 'approved' | 'rejected' | 'completed' | 'cancelled'
  due_date?: number
  related_control_id?: string
  control_number?: string
  control_name?: string
  evidence_count?: number
  source?: 'manual' | 'auto-frequency' | 'auto-reminder'
  approval_required?: number
  created_at: number
  updated_at: number
}

interface TaskKanbanProps {
  tasks: Task[]
  onTaskClick: (task: Task) => void
  onStatusChange: (task: Task, newStatus: Task['status']) => Promise<void>
  loading?: boolean
}

// Kanban column configuration
// Maps database statuses to user-friendly kanban columns
type KanbanColumn = 'not-started' | 'in-progress' | 'completed' | 'blocked'

interface ColumnConfig {
  id: KanbanColumn
  title: string
  statuses: Task['status'][] // Database statuses that map to this column
  color: string
  bgColor: string
  borderColor: string
  icon: typeof Clock
}

const COLUMNS: ColumnConfig[] = [
  {
    id: 'not-started',
    title: 'Not Started',
    statuses: ['open'],
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    icon: Clock,
  },
  {
    id: 'in-progress',
    title: 'In Progress',
    statuses: ['in-progress', 'pending-approval', 'approved', 'rejected'],
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    icon: PlayCircle,
  },
  {
    id: 'completed',
    title: 'Completed',
    statuses: ['completed'],
    color: 'text-green-700',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    icon: CheckCircle,
  },
  {
    id: 'blocked',
    title: 'Blocked/Cancelled',
    statuses: ['cancelled'],
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    icon: XCircle,
  },
]

// Map kanban columns to database status for drag-drop
const COLUMN_TO_STATUS: Record<KanbanColumn, Task['status']> = {
  'not-started': 'open',
  'in-progress': 'in-progress',
  'completed': 'completed',
  'blocked': 'cancelled',
}

// ============================================================================
// HELPERS
// ============================================================================

const priorityConfig: Record<string, { label: string; color: string }> = {
  critical: { label: 'Critical', color: 'bg-red-100 text-red-800 border-red-200' },
  high: { label: 'High', color: 'bg-orange-100 text-orange-800 border-orange-200' },
  medium: { label: 'Medium', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  low: { label: 'Low', color: 'bg-gray-100 text-gray-800 border-gray-200' },
}

const statusBadgeConfig: Record<string, { label: string; color: string; icon: typeof Clock }> = {
  open: { label: 'Open', color: 'bg-blue-100 text-blue-800', icon: Clock },
  'in-progress': { label: 'In Progress', color: 'bg-amber-100 text-amber-800', icon: PlayCircle },
  'pending-approval': { label: 'Pending Approval', color: 'bg-purple-100 text-purple-800', icon: Send },
  approved: { label: 'Approved', color: 'bg-teal-100 text-teal-800', icon: ThumbsUp },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: ThumbsDown },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-600', icon: XCircle },
}

const typeConfig: Record<string, { label: string; icon: typeof ClipboardList }> = {
  'evidence-due': { label: 'Evidence Due', icon: FileText },
  'document-review': { label: 'Document Review', icon: File },
  'risk-assessment': { label: 'Risk Assessment', icon: AlertTriangle },
  'vendor-assessment': { label: 'Vendor Assessment', icon: Building2 },
  custom: { label: 'Custom', icon: ClipboardList },
}

const formatDate = (timestamp?: number) => {
  if (!timestamp) return null
  return new Date(timestamp).toLocaleDateString()
}

const isOverdue = (task: Task) => {
  if (!task.due_date) return false
  if (task.status === 'completed' || task.status === 'cancelled') return false
  return task.due_date < Date.now()
}

// Get initials from username for avatar
const getInitials = (name?: string) => {
  if (!name) return '?'
  const parts = name.split(' ')
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}

// ============================================================================
// TASK CARD COMPONENT
// ============================================================================

interface TaskCardProps {
  task: Task
  onClick: () => void
  isDragging: boolean
}

function TaskCard({ task, onClick, isDragging }: TaskCardProps) {
  const overdue = isOverdue(task)
  const statusBadge = statusBadgeConfig[task.status] || statusBadgeConfig.open
  const StatusIcon = statusBadge.icon
  const TypeIcon = typeConfig[task.type]?.icon || ClipboardList

  return (
    <div
      onClick={onClick}
      className={`
        bg-white rounded-lg border shadow-sm p-3 cursor-pointer
        transition-all duration-200 hover:shadow-md
        ${isDragging ? 'shadow-lg ring-2 ring-primary-400' : ''}
        ${overdue ? 'border-red-300 bg-red-50' : 'border-gray-200'}
      `}
    >
      {/* Header: Priority + Status */}
      <div className="flex items-center gap-1.5 mb-2 flex-wrap">
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${priorityConfig[task.priority]?.color || ''}`}>
          {priorityConfig[task.priority]?.label}
        </span>
        {/* Show sub-status badge for in-progress column tasks */}
        {['pending-approval', 'approved', 'rejected'].includes(task.status) && (
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${statusBadge.color}`}>
            <StatusIcon className="h-3 w-3 mr-0.5" />
            {statusBadge.label}
          </span>
        )}
        {overdue && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
            <AlertCircle className="h-3 w-3 mr-0.5" />
            Overdue
          </span>
        )}
      </div>

      {/* Title */}
      <h4 className="text-sm font-medium text-gray-900 line-clamp-2 mb-2">
        {task.title}
      </h4>

      {/* Description preview */}
      {task.description && (
        <p className="text-xs text-gray-500 line-clamp-2 mb-2">
          {task.description}
        </p>
      )}

      {/* Footer: Due date, Type, Control link */}
      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
        {task.due_date && (
          <div className={`flex items-center ${overdue ? 'text-red-600 font-medium' : ''}`}>
            <Calendar className="h-3 w-3 mr-0.5" />
            {formatDate(task.due_date)}
          </div>
        )}
        <div className="flex items-center">
          <TypeIcon className="h-3 w-3 mr-0.5" />
          {typeConfig[task.type]?.label || 'Task'}
        </div>
        {task.control_number && (
          <Link
            to={`/controls/${task.related_control_id}`}
            className="flex items-center text-primary-600 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            <Shield className="h-3 w-3 mr-0.5" />
            {task.control_number}
          </Link>
        )}
      </div>

      {/* Assignee avatar */}
      <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
        <div className="flex items-center gap-1.5">
          {task.assigned_to ? (
            <>
              <div className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-medium">
                {getInitials(task.assigned_to_username)}
              </div>
              <span className="text-xs text-gray-600 truncate max-w-[100px]">
                {task.assigned_to_username}
              </span>
            </>
          ) : (
            <>
              <User className="h-4 w-4 text-gray-400" />
              <span className="text-xs text-gray-400">Unassigned</span>
            </>
          )}
        </div>
        {task.evidence_count && task.evidence_count > 0 && (
          <div className="flex items-center text-xs text-gray-400">
            <FileText className="h-3 w-3 mr-0.5" />
            {task.evidence_count}
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// KANBAN COLUMN COMPONENT
// ============================================================================

interface KanbanColumnProps {
  column: ColumnConfig
  tasks: Task[]
  onTaskClick: (task: Task) => void
}

function KanbanColumn({ column, tasks, onTaskClick }: KanbanColumnProps) {
  const Icon = column.icon

  return (
    <div className={`flex flex-col min-w-[280px] max-w-[320px] ${column.bgColor} rounded-lg border ${column.borderColor}`}>
      {/* Column Header */}
      <div className={`flex items-center justify-between px-3 py-2.5 border-b ${column.borderColor}`}>
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${column.color}`} />
          <h3 className={`font-medium text-sm ${column.color}`}>{column.title}</h3>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${column.bgColor} ${column.color} border ${column.borderColor}`}>
          {tasks.length}
        </span>
      </div>

      {/* Droppable Area */}
      <Droppable droppableId={column.id}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`
              flex-1 p-2 space-y-2 overflow-y-auto min-h-[200px] max-h-[calc(100vh-340px)]
              transition-colors duration-200
              ${snapshot.isDraggingOver ? 'bg-primary-50' : ''}
            `}
          >
            {tasks.length === 0 && !snapshot.isDraggingOver ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                No tasks
              </div>
            ) : (
              tasks.map((task, index) => (
                <Draggable key={task.id} draggableId={task.id} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <TaskCard
                        task={task}
                        onClick={() => onTaskClick(task)}
                        isDragging={snapshot.isDragging}
                      />
                    </div>
                  )}
                </Draggable>
              ))
            )}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  )
}

// ============================================================================
// MAIN KANBAN COMPONENT
// ============================================================================

export default function TaskKanban({ tasks, onTaskClick, onStatusChange, loading }: TaskKanbanProps) {
  const [dragging, setDragging] = useState(false)

  // Group tasks by kanban column
  const tasksByColumn = useMemo(() => {
    const result: Record<KanbanColumn, Task[]> = {
      'not-started': [],
      'in-progress': [],
      'completed': [],
      'blocked': [],
    }

    tasks.forEach(task => {
      const column = COLUMNS.find(col => col.statuses.includes(task.status))
      if (column) {
        result[column.id].push(task)
      }
    })

    // Sort tasks within each column by priority (critical first), then by due date
    const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 }
    Object.keys(result).forEach(colId => {
      result[colId as KanbanColumn].sort((a, b) => {
        const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority]
        if (priorityDiff !== 0) return priorityDiff
        // Then by due date (earliest first, null last)
        if (a.due_date && b.due_date) return a.due_date - b.due_date
        if (a.due_date) return -1
        if (b.due_date) return 1
        return 0
      })
    })

    return result
  }, [tasks])

  // Handle drag end
  const handleDragEnd = async (result: DropResult) => {
    setDragging(false)

    const { source, destination, draggableId } = result

    // Dropped outside a droppable area
    if (!destination) return

    // Dropped in the same position
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return
    }

    // Find the task
    const task = tasks.find(t => t.id === draggableId)
    if (!task) return

    // Get the new status based on destination column
    const destColumnId = destination.droppableId as KanbanColumn
    const newStatus = COLUMN_TO_STATUS[destColumnId]

    // Only update if status actually changed
    if (!COLUMNS.find(col => col.id === destColumnId)?.statuses.includes(task.status)) {
      await onStatusChange(task, newStatus)
    }
  }

  const handleDragStart = () => {
    setDragging(true)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Drag hint */}
      {dragging && (
        <div className="text-center text-sm text-gray-500 py-1">
          Drop task in a column to change its status
        </div>
      )}

      {/* Kanban Board */}
      <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
        <div className="flex gap-4 overflow-x-auto pb-4">
          {COLUMNS.map(column => (
            <KanbanColumn
              key={column.id}
              column={column}
              tasks={tasksByColumn[column.id]}
              onTaskClick={onTaskClick}
            />
          ))}
        </div>
      </DragDropContext>

      {/* Empty state */}
      {tasks.length === 0 && (
        <div className="text-center py-12">
          <ClipboardList className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No tasks to display</p>
          <p className="text-sm text-gray-400 mt-1">
            Create your first task to see it on the board
          </p>
        </div>
      )}
    </div>
  )
}
